// Relative "time since" labels driven by one shared clock.
//
// Every mounted label subscribes to a single module-level ticker instead of
// owning its own interval. The ticker runs only while something is subscribed,
// pauses while the tab is hidden, and a label re-renders only when its string
// actually changes (setState bails out on an equal value), so a long feed
// costs one timer and a few string compares per second.
import { memo, useEffect, useMemo, useState } from "react";
import { formatUserDateTimeWithZone, parseUtcTimestampToMs } from "./timezone";

const TICK_MS = 1000;
const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

let nowMs = Date.now();
let timerId;
const listeners = new Set();

const emit = () => {
  nowMs = Date.now();
  listeners.forEach((listener) => listener());
};

const startTicker = () => {
  if (timerId !== undefined || document.hidden) return;
  emit();
  timerId = window.setInterval(emit, TICK_MS);
};

const stopTicker = () => {
  if (timerId === undefined) return;
  window.clearInterval(timerId);
  timerId = undefined;
};

const handleVisibilityChange = () => {
  if (document.hidden) stopTicker();
  else startTicker();
};

const subscribe = (listener) => {
  listeners.add(listener);
  if (listeners.size === 1) {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    startTicker();
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopTicker();
    }
  };
};

// While the ticker is idle (no subscribers yet, or tab hidden) the cached
// clock is stale, so read the real one.
const readNow = () => (timerId === undefined ? (nowMs = Date.now()) : nowMs);

/** "Just now" under 10s, then 10s steps, then whole minutes, hours, days. */
export const formatTimeSince = (timestampMs, now) => {
  if (!Number.isFinite(timestampMs)) return "";

  const elapsed = Math.max(0, now - timestampMs);
  if (elapsed < 10 * SECOND_MS) return "Just now";
  if (elapsed < MINUTE_MS) {
    return `${Math.floor(elapsed / (10 * SECOND_MS)) * 10}s ago`;
  }
  if (elapsed < HOUR_MS) return `${Math.floor(elapsed / MINUTE_MS)}m ago`;
  if (elapsed < DAY_MS) return `${Math.floor(elapsed / HOUR_MS)}h ago`;
  return `${Math.floor(elapsed / DAY_MS)}d ago`;
};

export const useTimeSince = (timestamp) => {
  const timestampMs = useMemo(() => parseUtcTimestampToMs(timestamp), [timestamp]);
  const [state, setState] = useState(() => ({
    timestampMs,
    label: formatTimeSince(timestampMs, readNow()),
  }));

  useEffect(() => {
    const update = () => {
      const label = formatTimeSince(timestampMs, nowMs);
      setState((prev) =>
        prev.timestampMs === timestampMs && prev.label === label
          ? prev
          : { timestampMs, label },
      );
    };
    // Subscribing starts the ticker (refreshing nowMs) if it was idle, so
    // sync once afterwards; equal strings bail out without re-rendering.
    const unsubscribe = subscribe(update);
    update();
    return unsubscribe;
  }, [timestampMs]);

  // Never show a previous item's label, not even for the frame before the
  // effect resyncs.
  return state.timestampMs === timestampMs
    ? state.label
    : formatTimeSince(timestampMs, readNow());
};

// Isolated so the shared clock re-renders only this label, never the card or
// the feed. The absolute time stays available on hover.
export const TimeSince = memo(({ timestamp, className }) => {
  const label = useTimeSince(timestamp);
  if (!label) return null;

  return (
    <time className={className} title={formatUserDateTimeWithZone(timestamp)}>
      {label}
    </time>
  );
});

TimeSince.displayName = "TimeSince";
